import {useEffect, useState} from "react";
import styles from "./Toast.module.scss";
import toastGeneralIcon from "../assets/images/common/toast_general_icon.png";

export type ToastPosition = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

export type ToastType = "general";

const DEFAULT_POSITION: ToastPosition = {
  top: 24,
  bottom: 0,
  left: 0,
  right: 0,
};

type ToastPhase = "visible" | "exiting" | "hidden";

type ToastProps = {
  message: string;
  position?: ToastPosition;
  durationSeconds?: number;
  type?: ToastType;
};

const getPositionStyle = (position: ToastPosition): React.CSSProperties => {
  const {top, bottom, left, right} = position;
  const style: React.CSSProperties = {};

  if (top !== undefined) {
    style.top = `${top}px`;
  }
  if (bottom !== undefined) {
    style.bottom = `${bottom}px`;
  }
  const isHorizontalCenter =
    (left === 0 || left === undefined) && (right === 0 || right === undefined);
  if (isHorizontalCenter) {
    style.left = "50%";
    style.transform = "translateX(-50%)";
  }
  if (!isHorizontalCenter && left !== undefined) {
    style.left = `${left}px`;
  }
  if (!isHorizontalCenter && right !== undefined) {
    style.right = `${right}px`;
  }

  return style;
};

const Toast = ({
  message,
  position = DEFAULT_POSITION,
  durationSeconds = 3,
  type = "general",
}: ToastProps) => {
  const [phase, setPhase] = useState<ToastPhase>("visible");

  useEffect(() => {
    setPhase("visible");
    const timer = window.setTimeout(
      () => {
        setPhase("exiting");
      },
      durationSeconds * 1000 + 500,
    );
    return () => window.clearTimeout(timer);
  }, [message, durationSeconds]);

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.animationName === "fadeOut") {
      setPhase("hidden");
    }
  };

  const typeClass = type === "general" ? styles.general : "";
  const positionStyle = getPositionStyle(position);

  if (phase === "hidden") {
    return null;
  }

  const iconSrc = type === "general" ? toastGeneralIcon : null;

  return (
    <div
      className={`${styles.toast} ${typeClass} ${phase === "exiting" ? styles.exiting : ""}`}
      style={positionStyle}
      role="alert"
      onAnimationEnd={handleAnimationEnd}
    >
      {iconSrc && (
        <img
          src={iconSrc}
          alt=""
          className={styles.icon}
          width={16}
          height={16}
        />
      )}
      <p className={styles.message}>{message}</p>
    </div>
  );
};

export default Toast;
